import { Plus } from 'lucide-react';
import { setSelectedBoardId } from '@common-reducers/BoardsThunks';
import { setIsImportModalOpen, setBulkAudiencesListSearchTerm, setActiveTab, setActiveBulkView, setPreviousState, type BulkView } from '@common-reducers/CampaignReducer';
import type { AppThunkDispatch } from '@common/types/common-root-state-type';
import { useCampaignsRedesignNav } from '@extension/context/content/components/campaigns-redesign/nav-context';
import { selectNumberOfAudienceSelected, selectBulkAudiencesListSearchTerm } from '@common-reducers/CampaignSelectors';
import { DBBoardsThunks, boardsSelectByQuery } from "@common-reducers/DBServiceThunks";
import { MODAL, modalActions } from '@common-reducers/ModalReducer';
import { pickerDialogActions } from '@common-reducers/PickerDialogReducer';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import BulkButton from '../bulk-button';
import BulkSearchField from '../bulk-search-field';
import CampaignTabHeader from '../campaign-tab-header';
import LazyLoadingVirtuoso from '../lazy-loading-virtuoso';
import BulkAudienceSelectRow from './bulk-audience-select-row';
import ImportCSVOrGoogleSheets from './import-csv-or-google-sheets';
import { importItemsActions } from '@common-reducers/ImportItemsReducer';
import { generateNewAudienceName } from '@common-services/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';

// The full "open the New-Audience import modal" Redux sequence, extracted so the
// redesign's URL-flag effect (campaign-state-bridge → ManagementViewAdapter) opens
// the modal through the SAME path as the button — SSoT, no drift. Kept here (not in
// CampaignThunks) to preserve the original "avoid circular dependency" note below.
export function openNewAudienceModal(dispatch: AppThunkDispatch, activeBulkView: BulkView) {
    dispatch(setSelectedBoardId({ boardId: '' }));

    // Manual state backup and navigation to avoid circular dependency with CampaignThunks
    dispatch(setPreviousState({
        activeTab: 'audiences', // We know we are in audiences tab
        activeBulkView: activeBulkView
    }));

    if (activeBulkView === "campaignManagementView") {
        dispatch(setActiveTab('audiences'))
        dispatch(setActiveBulkView("campaignNewAudienceFromManagementView"))
    }
    else {
        dispatch(setActiveBulkView("campaignNewAudienceFromNewCampaignView"))
    }

    dispatch(modalActions.setComponentToRender(MODAL.IMPORT_EXCEL_TO_BLUETICKS));
    dispatch(pickerDialogActions.closePickerDialog());
    dispatch(modalActions.setOpenedby('campaign'));
    dispatch(setIsImportModalOpen(true));
    dispatch(importItemsActions.setNewAudienceName(generateNewAudienceName()));
}

export default function BulkAudiencesList() {
    const dispatch = useAppDispatch();
    const redesignNav = useCampaignsRedesignNav();
    const { t } = useTranslation(namespace);
    const selectedAudience = useAppSelector((state) => selectNumberOfAudienceSelected(state));
    const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);
    const searchTerm = useAppSelector(selectBulkAudiencesListSearchTerm);
    const handleSearchChange = (value: string) => {
        dispatch(setBulkAudiencesListSearchTerm(value));
    };

    const handleclickNewAudience = () => {
        // Redesign management tab: drive the modal's open state through the URL so
        // reload/back restore it. (Selection mode — the wizard's audience step —
        // keeps opening the modal in place: it's a step within an already-routed
        // flow, navigating away would leave the wizard.)
        if (redesignNav && activeBulkView === "campaignManagementView") {
            redesignNav.newAudience();
            return;
        }
        openNewAudienceModal(dispatch, activeBulkView);
    };

    const selectedString = (selectedAudience !== 0) ? t('audiences.selected', { count: selectedAudience }) : t('audiences.noneSelected');

    // In the management tab the parent is a flex column with a definite height, so the
    // list fills the available space (taller table, no page scroll). In the new-campaign
    // flow the parent doesn't stretch its children vertically, so keep a fixed height.
    const fillHeight = activeBulkView === "campaignManagementView";

    return (
        <div className={`flex flex-col items-center w-full max-w-[750px] mx-auto px-6 ${fillHeight ? 'flex-1 min-h-0 pb-6' : 'pb-12'}`}>
            <CampaignTabHeader
                title={activeBulkView === "campaignManagementView" ? t('campaign.manageAudiences') : t('campaign.whoToSend')}
                subTitle={activeBulkView === "campaignManagementView" ? t('campaign.manageAudiencesSubtitle') : t('campaign.selectAudiencesSubtitle')} />

            <div className={`flex flex-col w-full ${fillHeight ? 'flex-1 min-h-0' : ''}`}>
                <div className={`w-full flex flex-col ${fillHeight ? 'flex-1 min-h-0' : ''}`}>
                    <div className="flex items-center gap-3 mt-8 mb-6">
                        <BulkSearchField
                            searchTerm={searchTerm}
                            setSearchTerm={handleSearchChange}
                            placeHolderString={t('audiences.searchPlaceholder')}
                            className="flex-1"
                        />
                        <BulkButton
                            onClick={handleclickNewAudience}
                            buttonString={t('audiences.newAudience')}
                            Icon={Plus}
                            buttonWidth='auto'
                        />
                    </div>

                    <div className={`bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden w-full ${fillHeight ? 'flex flex-col flex-1 min-h-0' : ''}`}>
                        {/* Status Bar */}
                        {activeBulkView === "newCampaignView" && (
                            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                                <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    {selectedString}
                                </p>
                            </div>
                        )}

                        <LazyLoadingVirtuoso
                            isFullWidth={true}
                            height={fillHeight ? "100%" : 400}
                            rowHeightString='auto'
                            filterObj={{
                                'name': { $regex: searchTerm, $options: 'i' },
                                type: 'audience',
                            }}
                            DBThunks={DBBoardsThunks}
                            DBReducer={state => state.DBBoardsReducer}
                            selectByQuery={boardsSelectByQuery}
                            Component={BulkAudienceSelectRow}
                        />
                    </div>
                </div>
            </div>
            <ImportCSVOrGoogleSheets />
        </div>
    )
}
